import { CustomerChannel } from "../../../customers/Customer";
import { IndexedObject } from "../../../products/channel-tab/compareConfigs";
import { updateChannelSectionSettings } from "../../../api/channelConfigApi";
import { SectionChannelSettingsValue } from "./SectionChannelSettings";

// Matches the slug convention used by generic_textpattern.py when it looks up
// section_settings by section name.
export const slugifySectionName = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");

type SectionSettingsMap = { [key: string]: IndexedObject };

const getSectionSettingsMap = (
  channel: CustomerChannel
): SectionSettingsMap => {
  const config = channel.config as IndexedObject | null;
  return (config?.section_settings as SectionSettingsMap) ?? {};
};

export const getChannelsConnectedToStructure = (
  allChannels: CustomerChannel[] | undefined,
  structureId: number | undefined
): CustomerChannel[] => {
  if (!allChannels || !structureId) return [];
  return allChannels.filter(
    (channel) => channel.document_structure_id === structureId
  );
};

// Reads settings for a section from the first connected channel that has
// them. Accepts either the exact section name or its slugified form as key.
export const readSectionSettings = (
  channels: CustomerChannel[],
  sectionName: string
): SectionChannelSettingsValue => {
  if (!channels.length || !sectionName) return {};
  const slug = slugifySectionName(sectionName);
  for (const channel of channels) {
    const sectionSettings = getSectionSettingsMap(channel);
    const match = sectionSettings[sectionName] ?? sectionSettings[slug];
    if (match && Object.keys(match).length > 0) {
      return { ...match } as SectionChannelSettingsValue;
    }
  }
  return {};
};

// Writes the given section settings to every connected channel, preserving
// other sections' settings. Uses the slugified key so it matches how
// generic_textpattern.py looks it up.
export const saveSectionSettingsToChannels = async (
  token: string,
  channels: CustomerChannel[],
  sectionName: string,
  settings: SectionChannelSettingsValue
): Promise<void> => {
  const slug = slugifySectionName(sectionName);
  await Promise.all(
    channels.map((channel) => {
      const next: SectionSettingsMap = { ...getSectionSettingsMap(channel) };
      delete next[sectionName];
      if (Object.keys(settings).length === 0) {
        delete next[slug];
      } else {
        next[slug] = settings as IndexedObject;
      }
      return updateChannelSectionSettings(token, channel.id, next);
    })
  );
};
